import * as queryString from "query-string";
import {
  readBrowserLocation,
  setAggReport,
  setFilterValue,
  addDisplayField,
  setSortOrder,
} from "./query";
import { StoreState } from "../types";
import {
  addToMultiFilterValue,
  removeFromMultiFilterValue,
  fetchResults,
  setBrowserLocation,
} from ".";
import { receiveFieldsFailure } from "./results";
import { apiFieldMappings } from "../../refdata/FilterOptions";
import { allValuesForFilter } from "./../../common/services/Filters";
import {
  historicalDisplayFields,
  failuresDiplayFields,
} from "./../../refdata/FilterOptions";

export function decodeBrowserPathToState(
  store: any,
  locationSearchString: string,
) {
  return function(dispatch: any, getState: any) {
    // Abort if the browser location already matches the state
    const existingParams = store.getState().query.urls.browserPath;
    if (existingParams === locationSearchString) {
      return;
    }
    // Otherwise, proceed by first parsing the string
    dispatch(readBrowserLocation());
    const params = queryString.parse(locationSearchString);
    // Process params
    let inputFields: any;
    inputFields = params.displayFields;

      if (params && isValidParam.displayFields(inputFields)) {
        // dispatch actions to set individual filter/query values
        setStateFromParams(store, params);
        // update the browser path in state
        store.dispatch(setBrowserLocation(locationSearchString));
        // finally, trigger the api fetch based on updated values
        store.dispatch(
          fetchResults(store.getState().query, store.getState().config.apiPath),
        );
      } else {
        // Return error to client
        // TODO: remove console.log()
        console.log("Query params validation failed ");
        let error = new Error();
        store.dispatch(receiveFieldsFailure(error)); 
      }
    return;
  };
}

export const isValidParam = {
  // Parameter validation functions
  // We don't want to trust the quality of what is really user input
  // Each accepts a value and returns true (it's valid) or false (it's invalid)
  aggReport(val: any) {
    const values = ["by_state", "by_year", "detail"];
    return values.indexOf(val) > -1;
  },
  year(val: any) {
    const currentYear = new Date().getFullYear();
    return Number(val) >= 1934 && Number(val) <= currentYear;
  },
  multiFilter(val: any, filterName: string) {
    let valid = true;
    const possibleValues = allValuesForFilter(filterName);
    const vals = val.split(",");
    vals.forEach((val: string) => {
      if (possibleValues.indexOf(val) === -1) {
        valid = false;
      }
    });
    return valid;
  },
  singleValue(val: any, filterName: string) {
    let valid = true;
    const possibleValues = allValuesForFilter(filterName);
    if (possibleValues.indexOf(val) === -1) {
      valid = false;
    }
    return valid;
  },
  sort(sortField: string, sortOrder: string) {
    let valid = true;
    const orderValues = ["asc", "desc"];
    if (orderValues.indexOf(sortOrder) === -1) {
      valid = false;
    }
    if (sortField.length === 0) {
      valid = false;
    }
    return valid;
  },

  displayFields(val: string) {
    if (val === undefined || val.length === 0) return false;

    const listOfDisplayFields = val.split(",");

    //identify which app display list to check

    const allDisplayFields = [
      ...historicalDisplayFields,
      ...failuresDiplayFields,
    ];

    //return false poofing fields are not present
    //return true if spoofing fields present
    let badFields = listOfDisplayFields.some(
      r => allDisplayFields.indexOf(r) < 0,
    );
    // TODO: remove console.log()
    console.log(`Invalid Fields Present: ${  badFields}`);

    return !badFields;
  },
};

export const setStateFromParams = (store: any, params: any) => {
  // NOTE: We only use the simple actions to trigger reducers,
  //       not compound actions that also trigger an API fetch.
  //       The fetch is called separately
  // Range Selects (e.g. years)
  apiFieldMappings.rangeSelect.forEach((f: any) => {
    if (
      params[f.filterNameStart] &&
      params[f.filterNameEnd] &&
      isValidParam.singleValue(params[f.filterNameStart], f.filterNameStart) &&
      isValidParam.singleValue(params[f.filterNameEnd], f.filterNameEnd)
    ) {
      store.dispatch(
        setFilterValue(f.filterNameStart, params[f.filterNameStart]),
      );
      store.dispatch(setFilterValue(f.filterNameEnd, params[f.filterNameEnd]));
    }
  });
  // Multi Selects (e.g. US states)
  apiFieldMappings.multiSelect.forEach((f: any) => {
    const filterName = f.filterName;
    const newValue = params[filterName];
    if (newValue && isValidParam.multiFilter(newValue, filterName)) {
      // first clear out any residual values (certain back button events)...
      store.dispatch(
        removeFromMultiFilterValue(
          f.filterName,
          allValuesForFilter(f.filterName),
        ),
      );
      // ... then add in new values from url
      const newVals = newValue.split(",");
      if (newVals && newVals.length > 0) {
        store.dispatch(addToMultiFilterValue(filterName, newVals));
      }
    }
  });
  // Single Values (e.g. CB/SI)
  apiFieldMappings.singleValues.forEach((f: any) => {
    const filterName = f.filterName;
    const newValue = params[filterName];
    if (newValue && isValidParam.singleValue(newValue, filterName)) {
      store.dispatch(setFilterValue(filterName, newValue));
    }
  });
  // Aggregate Report
  if (isValidParam.aggReport(params.aggReport)) {
    const aggReport = params.aggReport as StoreState.Query["aggReport"];
    store.dispatch(setAggReport(aggReport));
  }

  // Sort
  if (
    params.sortField &&
    params.sortOrder &&
    isValidParam.sort(params.sortField, params.sortOrder)
  ) {
    store.dispatch(setSortOrder(params.sortField, params.sortOrder));
  }

  // Display Fields
  if (
    params.displayFields &&
    isValidParam.displayFields(params.displayFields)
  ) {
    const fields = params.displayFields.split(",");
    store.dispatch(addDisplayField(fields, true));
  }
};
